import { memo } from 'react';

import { useHistory } from 'react-router-dom';

import { Campaign, CampaignChannels } from 'domains/campaigns/types';
import MoreOptionsDropdown, { MenuItemProps } from 'domains/core/components/MoreOptionsDropdown';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import { CampaignStatus, URLPaths } from 'models/enums';

import useGetCampaignMenuItem from '../useGetCampaignMenuItem';

/** Live-campaign menu label (distinct from "Edit") so RBAC can key off `name` only. */
const CONVERT_TO_DRAFT_MENU_NAME = 'Convert to draft';

export type CampaignsTableOptionsCellProps = {
    campaign: Campaign;
    isConvertingToDraft: boolean;
    refetch: () => void;
    setCampaign: (campaign: Campaign) => void;
    setErrorMessage: (message: string) => void;
    setInfoMessage: (message: string) => void;
    setIsDeleteModalOpen: (isOpen: boolean) => void;
    setIsPauseModalOpen: (isOpen: boolean) => void;
    setIsResumeModalOpen: (isOpen: boolean) => void;
    setPendingEditCampaign: (campaign: { id: string; name: string } | null) => void;
    setSuccessMessage: (message: string) => void;
    toggleUpdateCampaignModal: (params: {
        campaign?: Campaign;
        onClick: (isModalOpen: boolean) => void;
        onConfirm?: () => void;
    }) => void;
};

/**
 * A memoized component that renders the options dropdown cell for each campaign row.
 * This component properly calls hooks at the top level, fixing the React hooks violation
 * that occurred when hooks were called inside the Cell renderer function.
 */
const CampaignsTableOptionsCell = ({
    campaign,
    isConvertingToDraft,
    refetch,
    setCampaign,
    setErrorMessage,
    setInfoMessage,
    setIsDeleteModalOpen,
    setIsPauseModalOpen,
    setIsResumeModalOpen,
    setPendingEditCampaign,
    setSuccessMessage,
    toggleUpdateCampaignModal,
}: CampaignsTableOptionsCellProps) => {
    const history = useHistory();
    const { canViewCurations, canManageCurationsDrafts, canPublishCurations } = useAccessCentrePermissions();
    const campaignNameString = `"${campaign.name}"`;

    const sharedMenuItemProps = {
        campaign,
        refetch,
        resetInfoMessage: () => setInfoMessage(''),
        setCampaign,
        setErrorMessage,
    };

    const cancelCampaignMenuItem = useGetCampaignMenuItem({
        name: 'Cancel',
        setInfoMessage: () => setInfoMessage(`Canceling ${campaignNameString}`),
        setSuccessMessage: () => setSuccessMessage(`${campaignNameString} canceled.`),
        testId: 'cancel-curation-menu-item',
        ...sharedMenuItemProps,
    });

    const stopCampaignMenuItem = useGetCampaignMenuItem({
        name: 'Stop',
        setInfoMessage: () => setInfoMessage(`Stopping ${campaignNameString}`),
        setSuccessMessage: () => setSuccessMessage(`${campaignNameString} stopped.`),
        testId: 'stop-curation-menu-item',
        ...sharedMenuItemProps,
    });

    const finishCampaignMenuItem = useGetCampaignMenuItem({
        name: 'Finish',
        setInfoMessage: () => setInfoMessage(`Finishing ${campaignNameString}`),
        setSuccessMessage: () => setSuccessMessage(`${campaignNameString} is finished.`),
        ...sharedMenuItemProps,
    });

    const deleteCampaignMenuItem = {
        name: 'Delete',
        onClick: () => toggleUpdateCampaignModal({ campaign, onClick: setIsDeleteModalOpen }),
        testId: 'delete-curation-menu-item',
    };

    const pauseCampaignMenuItem = {
        name: 'Pause',
        onClick: () => toggleUpdateCampaignModal({ campaign, onClick: setIsPauseModalOpen }),
        testId: 'pause-curation-menu-item',
        tooltipTitle: 'Temporarily stop contacts where they are in the Curation flow.',
    };

    const resumeCampaignMenuItem = {
        name: 'Resume',
        onClick: () => toggleUpdateCampaignModal({ campaign, onClick: setIsResumeModalOpen }),
        testId: 'resume-curation-menu-item',
        tooltipTitle: 'Resume Curation Flow.',
    };

    const viewDetailsMenuItem = {
        name: 'View Details',
        onClick: () => history.push(`${URLPaths.CURATIONS}/${campaign.id}/summary`),
        testId: 'view-details-menu-item',
    };

    // Open builder directly (draft uses manage_drafts; paused uses publish — see canShowMenuItem).
    const editMenuItem = {
        name: 'Edit',
        onClick: () => history.push(`${URLPaths.CURATIONS_CUSTOM}/${campaign.id}?status=${campaign.status}`),
        testId: 'edit-curation-menu-item',
    };

    // Live/scheduled row: convert to draft first, then edit (curations.manage_drafts only).
    // Label is distinct from "Edit" so permission checks can use `name` only (same as other actions).
    const convertToDraftMenuItem = {
        name: CONVERT_TO_DRAFT_MENU_NAME,
        disabled: isConvertingToDraft,
        onClick: () => {
            setCampaign(campaign);
            setInfoMessage(`Converting ${campaignNameString} to draft for editing...`);
            // Setting this state triggers the useEffect which calls convertToDraftAndEdit
            setPendingEditCampaign({ id: campaign.id, name: campaign.name });
        },
        testId: 'edit-scheduled-curation-menu-item',
        tooltipTitle: 'Convert to draft and edit the curation.',
    };

    const getMenuItems = () => {
        const isCampaignSending = campaign.status === CampaignStatus.SENDING;
        const isCampaignFinishing = campaign.status === CampaignStatus.FINISHING;
        const isDraft = campaign.status === CampaignStatus.DRAFT;

        // For draft campaigns, show Edit option as the primary action
        if (isDraft) {
            return [editMenuItem, deleteCampaignMenuItem];
        }

        if (campaign.canCancel) {
            return [
                viewDetailsMenuItem,
                ...(campaign.canDraft ? [convertToDraftMenuItem] : []),
                cancelCampaignMenuItem,
                ...(campaign.canPause ? [pauseCampaignMenuItem] : []),
            ];
        }

        const canShowStopWithFinishOption =
            campaign.channel === CampaignChannels.EMAIL &&
            !campaign.abTestConfig &&
            campaign.canStop &&
            isCampaignSending;

        if (canShowStopWithFinishOption) {
            return [
                viewDetailsMenuItem,
                stopCampaignMenuItem,
                ...[finishCampaignMenuItem],
                ...(campaign.canPause ? [pauseCampaignMenuItem] : []),
            ];
        }

        // For Voice and SMS curations that can be stopped and paused
        const isVoiceOrSmsCuration =
            campaign.channel === CampaignChannels.VOICE_CALL || campaign.channel === CampaignChannels.SMS;
        if (isVoiceOrSmsCuration && campaign.canStop && isCampaignSending) {
            return [viewDetailsMenuItem, stopCampaignMenuItem, ...(campaign.canPause ? [pauseCampaignMenuItem] : [])];
        }

        if (campaign.canStop && isCampaignFinishing) {
            return [viewDetailsMenuItem, stopCampaignMenuItem];
        }

        if (campaign.canDelete) {
            return [viewDetailsMenuItem, deleteCampaignMenuItem];
        }

        if (campaign.canResume) {
            return [
                viewDetailsMenuItem,
                stopCampaignMenuItem,
                resumeCampaignMenuItem,
                ...(campaign.canEdit ? [editMenuItem] : []),
            ];
        }

        if (campaign.status === CampaignStatus.SENDING_AB_TEST || campaign.status === CampaignStatus.WINNER_SCHEDULED) {
            return [viewDetailsMenuItem, stopCampaignMenuItem];
        }

        return [viewDetailsMenuItem];
    };

    const rawMenuItems = getMenuItems();

    const canShowMenuItem = (item: { name: string }) => {
        const { name } = item;
        const status = campaign.status;

        if (name === 'View Details') {
            return canViewCurations;
        }

        if (name === 'Delete') {
            return canManageCurationsDrafts && (status === CampaignStatus.DRAFT || status === CampaignStatus.CANCELED);
        }

        if (name === CONVERT_TO_DRAFT_MENU_NAME) {
            return canManageCurationsDrafts;
        }

        if (name === 'Edit') {
            if (status === CampaignStatus.DRAFT || status === CampaignStatus.SCHEDULED) {
                return canManageCurationsDrafts;
            }
            if (status === CampaignStatus.PAUSED) {
                return canPublishCurations;
            }
            return false;
        }

        if (['Cancel', 'Stop', 'Finish', 'Pause', 'Resume'].includes(name)) {
            return canPublishCurations;
        }

        return true;
    };

    const menuItems = rawMenuItems.filter((item: MenuItemProps) => canShowMenuItem(item));

    if (!menuItems?.length) {
        return null;
    }

    return <MoreOptionsDropdown buttonTestId="curation-more-options" menuItems={menuItems} />;
};

export default memo(CampaignsTableOptionsCell);
